// src/features/analytics/retention.ts
//
// Pure transforms for the per-media audience-retention panel (metrics
// expansion). The wire buckets are server-computed SUFFIX data — each bucket's
// viewersReached counts sessions that watched at least to that bucket's start
// — so everything here is derivation and labeling, never re-aggregation.
// hasData=false responses (the server's <10-session low-sample guard) carry
// empty buckets and must never reach these helpers' chart path.

import type { MediaRetentionData, RetentionBucket } from './types';

/** The wire contract's fixed bucket count: 20 buckets of 5% of duration each. */
export const RETENTION_BUCKET_COUNT = 20;

export interface RetentionStats {
  /** Fraction 0..1 of sessions that reached the final bucket (95%+). */
  completionRate: number;
  /** Percent 0..100 of the content the median session watched. */
  medianWatchedPct: number;
}

export interface RetentionChartRow {
  bucket: number;
  pctStart: number;
  /** X-axis label: mm:ss into the content when duration is known, else "N%". */
  label: string;
  /** Always the percent form ("60%") — tooltips show it beside the timestamp. */
  pctLabel: string;
  /** mm:ss at the bucket start; null when contentDurationMs is 0 (unknown). */
  timeLabel: string | null;
  /** Percent 0..100 of sessions still watching at the bucket start. */
  pctViewers: number;
  viewersReached: number;
}

/**
 * "12:40"-style label from milliseconds (hours prefix past 59:59). Used for
 * retention bucket timestamps; seconds are rounded.
 */
export function formatMmSs(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

function bucketAt(buckets: RetentionBucket[], bucket: number): RetentionBucket | undefined {
  return buckets.find((b) => b.bucket === bucket);
}

/**
 * Headline stats derived client-side from the suffix buckets.
 *
 * Completion rate = viewersReached at the final bucket / totalSessions.
 *
 * Median watched pct rule: the pctStart of the FIRST bucket where
 * viewersReached drops to half the sessions or fewer (viewersReached <=
 * totalSessions / 2) — a linear step at bucket grain, not an interpolation.
 * When even the final bucket holds more than half the audience, the median
 * session watched past every bucket start and the median reads 100.
 *
 * Returns null when the payload cannot support honest stats (no sessions or a
 * missing final bucket — defensive; hasData=true responses arrive complete).
 */
export function retentionStats(data: MediaRetentionData): RetentionStats | null {
  const finalBucket = bucketAt(data.buckets, RETENTION_BUCKET_COUNT - 1);
  if (!data.hasData || data.totalSessions <= 0 || !finalBucket) return null;

  const half = data.totalSessions / 2;
  const medianBucket = [...data.buckets]
    .sort((a, b) => a.bucket - b.bucket)
    .find((b) => b.viewersReached <= half);

  return {
    completionRate: finalBucket.viewersReached / data.totalSessions,
    medianWatchedPct: medianBucket ? medianBucket.pctStart : 100,
  };
}

/**
 * Chart rows in bucket order. Labels are timestamps (bucket/20 of the content
 * duration) when the duration is known, percent-only when contentDurationMs
 * is 0. Percent-still-watching is exact (rounding is a display concern).
 */
export function retentionChartRows(data: MediaRetentionData): RetentionChartRow[] {
  if (!data.hasData || data.totalSessions <= 0) return [];
  const hasDuration = data.contentDurationMs > 0;

  return [...data.buckets]
    .sort((a, b) => a.bucket - b.bucket)
    .map((b) => {
      const pctLabel = `${b.pctStart}%`;
      const timeLabel = hasDuration
        ? formatMmSs((b.bucket / RETENTION_BUCKET_COUNT) * data.contentDurationMs)
        : null;
      return {
        bucket: b.bucket,
        pctStart: b.pctStart,
        label: timeLabel ?? pctLabel,
        pctLabel,
        timeLabel,
        pctViewers: (b.viewersReached / data.totalSessions) * 100,
        viewersReached: b.viewersReached,
      };
    });
}
